import { apiClient, PaginatedResponse } from '@/lib/api-client';
import {
  User,
  AuthTokens,
  LoginCredentials,
  RegisterData,
  Card,
  CardSearchFilters,
  PriceHistory,
  Listing,
  CreateListingData,
  UserLevel,
  XpHistoryEntry,
  TrophyDefinition,
  UserTrophy,
  TrophyWithProgress,
  UserReputation,
  LeaderboardEntry,
  AwardXpResult,
} from '@/types';

// Authentication Services
export const authService = {
  register: async (data: RegisterData): Promise<{ user: User; tokens: AuthTokens }> => {
    const response = await apiClient.post<{ user: User; tokens: AuthTokens }>('/auth/register', data);
    if (!response.data) throw new Error('Registration failed');
    return response.data;
  },

  login: async (credentials: LoginCredentials): Promise<{ user: User; tokens: AuthTokens }> => {
    const response = await apiClient.post<{ user: User; tokens: AuthTokens }>('/auth/login', credentials);
    if (!response.data) throw new Error('Login failed');
    return response.data;
  },

  logout: async (refreshToken: string): Promise<void> => {
    await apiClient.post('/auth/logout', { refreshToken });
  },

  refreshToken: async (refreshToken: string): Promise<AuthTokens> => {
    const response = await apiClient.post<{ tokens: AuthTokens }>('/auth/refresh', { refreshToken });
    if (!response.data) throw new Error('Token refresh failed');
    return response.data.tokens;
  },

  sendVerificationEmail: async (): Promise<void> => {
    await apiClient.post('/auth/send-verification');
  },

  verifyEmail: async (token: string): Promise<void> => {
    await apiClient.post('/auth/verify-email', { token });
  },

  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.post('/auth/forgot-password', { email });
  },

  resetPassword: async (token: string, password: string): Promise<void> => {
    await apiClient.post('/auth/reset-password', { token, password });
  },

  requestDeleteAccount: async (): Promise<void> => {
    await apiClient.post('/auth/request-delete-account');
  },

  confirmDeleteAccount: async (token: string): Promise<void> => {
    await apiClient.post('/auth/confirm-delete-account', { token });
  },
};

// Card Services
export const cardService = {
  searchCards: async (filters: CardSearchFilters): Promise<PaginatedResponse<Card>> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await apiClient.get<PaginatedResponse<Card>>(`/cards?${params.toString()}`);
    if (!response.data) throw new Error('Failed to fetch cards');
    return response.data;
  },

  getCardById: async (id: string): Promise<Card> => {
    const response = await apiClient.get<any>(`/cards/${id}`);
    if (!response.data) throw new Error('Card not found');
    return response.data.data ?? response.data;
  },

  getPriceHistory: async (id: string, days: number = 30): Promise<PriceHistory[]> => {
    const response = await apiClient.get<any>(`/cards/${id}/price-history?days=${days}`);
    if (!response.data) throw new Error('Failed to fetch price history');
    return response.data.data ?? response.data;
  },

  getPopularCards: async (limit: number = 50): Promise<Card[]> => {
    const response = await apiClient.get<any>(`/cards/popular?limit=${limit}`);
    if (!response.data) throw new Error('Failed to fetch popular cards');
    return response.data.data ?? response.data;
  },

  getGames: async (): Promise<string[]> => {
    const response = await apiClient.get<any>('/cards/games');
    if (!response.data) throw new Error('Failed to fetch games');
    return response.data.data ?? response.data;
  },

  getSetsByGame: async (game: string): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`/cards/games/${encodeURIComponent(game)}/sets`);
    if (!response.data) throw new Error('Failed to fetch sets');
    return response.data;
  },

  searchCardsByName: async (query: string): Promise<Card[]> => {
    const response = await apiClient.get<any>(`/cards?name=${encodeURIComponent(query)}&limit=20`);
    if (!response.data) throw new Error('Failed to search cards');
    // Backend returns { success, data: { cards, pagination } }
    return response.data.data?.cards ?? response.data.data ?? [];
  },

  importSetCards: async (game: string, set_name: string): Promise<{ imported: number }> => {
    const response = await apiClient.post<any>('/cards/sets/import', { game, set_name });
    return response.data?.data ?? { imported: 0 };
  },
};

// Listing Services (Note: These endpoints need to be implemented in the backend)
export const listingService = {
  getListings: async (filters?: any): Promise<any> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await apiClient.get<any>(`/listings?${params.toString()}`);
    if (!response.data) throw new Error('Failed to fetch listings');
    // apiClient returns response.data already, so response = { success, data: { data: [], pagination } }
    return response.data?.data ?? [];
  },

  getListingById: async (id: string): Promise<Listing> => {
    const response = await apiClient.get<any>(`/listings/${id}`);
    if (!response.data) throw new Error('Listing not found');
    return response.data.data ?? response.data;
  },

  createListing: async (data: CreateListingData): Promise<Listing> => {
    const response = await apiClient.post<any>('/listings', data);
    if (!response.data) throw new Error('Failed to create listing');
    return response.data.data ?? response.data;
  },

  updateListing: async (id: string, data: Partial<CreateListingData>): Promise<Listing> => {
    const response = await apiClient.put<Listing>(`/listings/${id}`, data);
    if (!response.data) throw new Error('Failed to update listing');
    return response.data;
  },

  deleteListing: async (id: string): Promise<void> => {
    await apiClient.delete(`/listings/${id}`);
  },

  getUserListings: async (userId: string): Promise<Listing[]> => {
    const url = userId ? `/users/${userId}/listings` : '/users/me/listings';
    const response = await apiClient.get<any>(url);
    if (!response.data) throw new Error('Failed to fetch user listings');
    if (Array.isArray(response.data)) return response.data;
    return response.data?.data ?? [];
  },

  /** Returns listings that match the authenticated user's wishlist. */
  getWishlistListings: async (): Promise<{
    listings: any[];
    total: number;
    meta?: { reason: string } | null;
  }> => {
    const response = await apiClient.get<any>('/listings?wishlist=true');
    if (!response.data) throw new Error('Failed to fetch wishlist listings');
    const d = response.data.data ?? response.data;
    return {
      listings: d.data ?? [],
      total: d.pagination?.total ?? 0,
      meta: d.meta ?? null,
    };
  },

  getCardListings: async (cardId: string): Promise<any[]> => {
    const response = await apiClient.get<any>(`/listings?card_id=${cardId}&limit=50`);
    return response.data?.data ?? response.data ?? [];
  },

  placeBid: async (listingId: string, amount: number): Promise<any> => {
    const response = await apiClient.post<any>(`/listings/${listingId}/bids`, { amount });
    if (!response.data) throw new Error('Failed to place bid');
    return response.data;
  },

  makeOffer: async (listingId: string, amount: number, message?: string): Promise<any> => {
    const response = await apiClient.post<any>(`/listings/${listingId}/offers`, { amount, message });
    if (!response.data) throw new Error('Failed to make offer');
    return response.data;
  },
};

// User Services
export const userService = {
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/users/me');
    if (!response.data) throw new Error('Failed to fetch user');
    return response.data;
  },

  getUserById: async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/users/${id}`);
    if (!response.data) throw new Error('User not found');
    return response.data;
  },

  updateUser: async (id: string, data: Partial<User>): Promise<User> => {
    const response = await apiClient.put<User>(`/users/${id}`, data);
    if (!response.data) throw new Error('Failed to update user');
    return response.data;
  },

  getMyCollection: async (filters?: { search?: string; sort?: string }): Promise<any> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.sort) params.append('sort', filters.sort);
    const response = await apiClient.get<any>(`/collections/me?${params.toString()}`);
    if (!response.data) throw new Error('Failed to fetch collection');
    return response.data;
  },

  searchUsers: async (query: string): Promise<User[]> => {
    const response = await apiClient.get<any>(`/users/search?q=${encodeURIComponent(query)}`);
    if (!response.data) throw new Error('Failed to search users');
    return response.data ?? [];
  },

  uploadAvatar: async (file: File): Promise<{ avatar_url: string }> => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await apiClient.post<{ avatar_url: string }>('/users/me/avatar', formData, {
      headers: { 'Content-Type': undefined as any },
    });
    if (!response.data) throw new Error('Failed to upload avatar');
    return response.data;
  },

  deleteAvatar: async (): Promise<void> => {
    await apiClient.delete('/users/me/avatar');
  },

  updateProfile: async (data: { first_name?: string; last_name?: string; bio?: string }): Promise<User> => {
    const response = await apiClient.put<User>('/users/me', data);
    if (!response.data) throw new Error('Failed to update profile');
    return response.data;
  },
};

// Review Services
export const reviewService = {
  getReviewsForUser: async (userId: string): Promise<any[]> => {
    const response = await apiClient.get<any>(`/users/${userId}/reviews`);
    if (!response.data) throw new Error('Failed to fetch reviews');
    return response.data ?? [];
  },

  submitReview: async (
    userId: string,
    data: { rating: number; comment?: string; review_type?: string }
  ): Promise<any> => {
    const response = await apiClient.post<any>(`/users/${userId}/reviews`, data);
    if (!response.data) throw new Error('Failed to submit review');
    return response.data.data ?? response.data;
  },
};

// Order Services (Note: These endpoints need to be implemented in the backend)
export const orderService = {
  createOrder: async (listingId: string, quantity: number, shippingAddress: any): Promise<any> => {
    const response = await apiClient.post('/orders', {
      listing_id: listingId,
      quantity,
      shipping_address: shippingAddress,
    });
    if (!response.data) throw new Error('Failed to create order');
    return response.data;
  },

  getOrders: async (): Promise<any[]> => {
    const response = await apiClient.get<any[]>('/orders');
    if (!response.data) throw new Error('Failed to fetch orders');
    return response.data;
  },

  getOrderById: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/orders/${id}`);
    if (!response.data) throw new Error('Order not found');
    return response.data;
  },
};

// Collection Services
export const collectionService = {
  getMyCollection: async (filters?: {
    search?: string;
    sort?: string;
    game?: string;
    set_name?: string;
    condition?: string;
    status?: 'for_sale' | 'not_listed' | 'sold' | 'for_trade';
    page?: number;
    limit?: number;
  }): Promise<any> => {
    const params = new URLSearchParams();
    if (filters?.search)    params.append('search',    filters.search);
    if (filters?.sort)      params.append('sort',      filters.sort);
    if (filters?.game)      params.append('game',      filters.game);
    if (filters?.set_name)  params.append('set_name',  filters.set_name);
    if (filters?.condition) params.append('condition', filters.condition);
    if (filters?.status)    params.append('status',    filters.status);
    if (filters?.page)      params.append('page',      String(filters.page));
    if (filters?.limit)     params.append('limit',     String(filters.limit));
    const response = await apiClient.get<any>(`/collections/me?${params.toString()}`);
    return {
      items: response.data?.items ?? response.data?.cards ?? [],
      cards: response.data?.items ?? response.data?.cards ?? [],
      stats: response.data?.stats ?? null,
      total: (response as any).pagination?.total ?? 0,
      pages: (response as any).pagination?.pages ?? 1,
    };
  },

  getMySetCompletion: async (game?: string): Promise<any[]> => {
    const params = game ? `?game=${encodeURIComponent(game)}` : '';
    const response = await apiClient.get<any>(`/collections/me/sets${params}`);
    return response.data ?? [];
  },

  getMyFilters: async (): Promise<{ games: string[]; sets: Record<string, string[]> }> => {
    const response = await apiClient.get<any>('/collections/me/filters');
    return response.data ?? { games: [], sets: {} };
  },

  getCardStatus: async (cardId: string): Promise<any[]> => {
    const response = await apiClient.get<any>(`/collections/me/card/${cardId}`);
    return response.data ?? [];
  },

  getUserCollection: async (userId: string, page = 1): Promise<any> => {
    const response = await apiClient.get<any>(`/collections/${userId}?page=${page}`);
    return {
      items: response.data ?? [],
      total: (response as any).pagination?.total ?? 0,
      pages: (response as any).pagination?.pages ?? 1,
    };
  },

  addCard: async (data: {
    card_id?: string;
    item_category?: string;
    item_name?: string;
    item_brand?: string;
    condition: string;
    quantity?: number;
    acquisition_price?: number | null;
    notes?: string | null;
    is_for_sale?: boolean;
    is_for_trade?: boolean;
  }): Promise<any> => {
    const response = await apiClient.post<any>('/collections', data);
    if (!response.data) throw new Error('Failed to add card to collection');
    return response.data;
  },

  updateItem: async (id: string, data: {
    quantity?: number;
    condition?: string;
    acquisition_price?: number | null;
    notes?: string | null;
    is_for_sale?: boolean;
    is_for_trade?: boolean;
    make_an_offer?: boolean;
  }): Promise<any> => {
    const response = await apiClient.patch<any>(`/collections/${id}`, data);
    if (!response.data) throw new Error('Failed to update collection item');
    return response.data;
  },

  removeItem: async (id: string): Promise<void> => {
    await apiClient.delete(`/collections/${id}`);
  },

  reorder: async (items: { id: string; sort_order: number }[]): Promise<void> => {
    await apiClient.patch('/collections/me/reorder', { items });
  },

  quickList: async (id: string, data: {
    price: number;
    listing_type?: string;
    quantity?: number;
    shipping_price?: number;
    description?: string;
    image_urls?: string[];
  }): Promise<any> => {
    const response = await apiClient.post<any>(`/collections/${id}/list`, data);
    if (!response.data) throw new Error('Failed to create listing');
    return response.data;
  },

  getSetCards: async (game: string, set_name: string): Promise<any[]> => {
    const params = new URLSearchParams({ game, set_name });
    const response = await apiClient.get<any>(`/collections/me/sets/all?${params}`);
    return response.data ?? [];
  },

  getMissingCards: async (game: string, set_name: string): Promise<any[]> => {
    const params = new URLSearchParams({ game, set_name });
    const response = await apiClient.get<any>(`/collections/me/sets/missing?${params}`);
    return response.data ?? [];
  },

  getFeatured: async (): Promise<any[]> => {
    const response = await apiClient.get<any>('/collections/me/featured');
    return response.data ?? [];
  },

  getPublicVault: async (userId: string): Promise<{
    displayCase: any[];
    bundleCache: any[];
    merchGallery: any[];
  }> => {
    const response = await apiClient.get<any>(`/collections/${userId}/vault`);
    return response.data ?? { displayCase: [], bundleCache: [], merchGallery: [] };
  },

  getShowcase: async (limit = 20): Promise<any[]> => {
    const response = await apiClient.get<any>(`/collections/showcase?limit=${limit}`);
    return response.data ?? [];
  },

  featureItem: async (id: string, featured: boolean): Promise<void> => {
    await apiClient.patch(`/collections/${id}/feature`, { featured });
  },

  findCardListings: async (cardId: string, excludeUserId?: string): Promise<any[]> => {
    const params = new URLSearchParams({ card_id: cardId });
    if (excludeUserId) params.append('exclude_user_id', excludeUserId);
    const response = await apiClient.get<any>(`/listings?${params.toString()}`);
    return response.data?.data ?? response.data ?? [];
  },
};

// Forum Services
export const forumService = {
  getCategories: async (): Promise<any[]> => {
    const response = await apiClient.get<any>('/forum/categories');
    if (!response.data) throw new Error('Failed to fetch forum categories');
    return response.data ?? [];
  },
  getCategoryThreads: async (slug: string, page = 1): Promise<any> => {
    const response = await apiClient.get<any>(`/forum/categories/${slug}/threads?page=${page}`);
    return {
      threads: response.data ?? [],
      totalPages: (response as any).pagination?.pages ?? 1,
    };
  },
  createThread: async (data: { category_id: string; title: string; body: string; poll?: any }): Promise<any> => {
    const response = await apiClient.post<any>('/forum/threads', data);
    if (!response.data) throw new Error('Failed to create thread');
    return response.data;
  },
  getThread: async (id: string, page = 1): Promise<any> => {
    const response = await apiClient.get<any>(`/forum/threads/${id}?page=${page}`);
    if (!response.data) throw new Error('Failed to fetch thread');
    return {
      thread: response.data,
      posts: response.data?.posts ?? [],
      poll: response.data?.poll ?? null,
      totalPages: (response as any).pagination?.pages ?? 1,
    };
  },
  createPost: async (threadId: string, body: string): Promise<any> => {
    const response = await apiClient.post<any>(`/forum/threads/${threadId}/posts`, { body });
    if (!response.data) throw new Error('Failed to create post');
    return response.data;
  },
  editPost: async (postId: string, body: string): Promise<any> => {
    const response = await apiClient.put<any>(`/forum/posts/${postId}`, { body });
    if (!response.data) throw new Error('Failed to edit post');
    return response.data;
  },
  reactToPost: async (postId: string, reaction: string): Promise<any> => {
    const response = await apiClient.post<any>(`/forum/posts/${postId}/react`, { reaction });
    if (!response.data) throw new Error('Failed to react');
    return response.data;
  },
  votePoll: async (pollId: string, optionId: string): Promise<any> => {
    const response = await apiClient.post<any>(`/forum/polls/${pollId}/vote`, { option_id: optionId });
    if (!response.data) throw new Error('Failed to vote');
    return response.data;
  },
  getCardInfo: async (cardName: string): Promise<any> => {
    const response = await apiClient.get<any>(`/forum/cards/${encodeURIComponent(cardName)}/info`);
    return response.data ?? {};
  },
  searchThreads: async (q: string, page = 1): Promise<any> => {
    const response = await apiClient.get<any>(`/forum/search?q=${encodeURIComponent(q)}&page=${page}`);
    return response.data ?? { threads: [], total: 0 };
  },
  getUserPosts: async (userId: string, limit = 5): Promise<any[]> => {
    const response = await apiClient.get<any>(`/forum/users/${userId}/posts?limit=${limit}`);
    return response.data ?? [];
  },
  getWishlistThread: async (cardName: string): Promise<any | null> => {
    const response = await apiClient.get<any>(`/forum/search?q=${encodeURIComponent(cardName)}&wishlist=true`);
    return response.data?.threads?.[0] ?? null;
  },
  deletePost: async (postId: string): Promise<void> => {
    await apiClient.delete(`/forum/posts/${postId}`);
  },
  deleteThread: async (threadId: string): Promise<void> => {
    await apiClient.delete(`/forum/threads/${threadId}`);
  },
  pinThread: async (threadId: string, pinned: boolean): Promise<any> => {
    const response = await apiClient.patch<any>(`/forum/threads/${threadId}/pin`, { pinned });
    return response.data;
  },
};

// Wishlist Services
export const wishlistService = {
  getItems: async (): Promise<any[]> => {
    const response = await apiClient.get<any>('/wishlist');
    if (!response.data) throw new Error('Failed to fetch wishlist');
    return response.data.data ?? response.data ?? [];
  },

  createItem: async (formData: FormData): Promise<any> => {
    const response = await apiClient.post<any>('/wishlist', formData, {
      headers: { 'Content-Type': undefined as any },
    });
    if (!response.data) throw new Error('Failed to add to wishlist');
    return response.data.data ?? response.data;
  },

  /** Quick-add from a listing card — no image upload needed. */
  quickAdd: async (payload: {
    card_name: string;
    card_image_url?: string;
    set_name?: string;
    price_mode?: string;
    price_max?: number;
  }): Promise<any> => {
    const fd = new FormData();
    fd.append('card_name', payload.card_name);
    if (payload.card_image_url) fd.append('card_image_url', payload.card_image_url);
    if (payload.set_name)       fd.append('set_name', payload.set_name);
    fd.append('price_mode', payload.price_mode ?? 'none');
    if (payload.price_max != null) fd.append('price_max', String(payload.price_max));
    const response = await apiClient.post<any>('/wishlist', fd, {
      headers: { 'Content-Type': undefined as any },
    });
    if (!response.data) throw new Error('Failed to add to wishlist');
    return response.data.data ?? response.data;
  },

  updateItem: async (
    id: string,
    data: {
      condition_min?: string | null;
      price_mode?: string;
      price_min?: number | null;
      price_max?: number | null;
    }
  ): Promise<any> => {
    const response = await apiClient.put<any>(`/wishlist/${id}`, data);
    if (!response.data) throw new Error('Failed to update wishlist item');
    return response.data.data ?? response.data;
  },

  /** Permanently remove the row. */
  deleteItem: async (id: string): Promise<void> => {
    await apiClient.delete(`/wishlist/${id}`);
  },

  /** Soft-delete: mark as found (is_active → false). */
  markAsFound: async (id: string): Promise<any> => {
    const response = await apiClient.put<any>(`/wishlist/${id}`, { is_active: false });
    if (!response.data) throw new Error('Failed to update wishlist item');
    return response.data.data ?? response.data;
  },

  /** Re-activate a fulfilled item (is_active → true). */
  reactivateItem: async (id: string): Promise<any> => {
    const response = await apiClient.put<any>(`/wishlist/${id}`, { is_active: true });
    if (!response.data) throw new Error('Failed to reactivate wishlist item');
    return response.data.data ?? response.data;
  },

  // Legacy aliases kept so existing hook bindings compile
  getWishlist: async (): Promise<any[]> => wishlistService.getItems(),
  addToWishlist: async (_cardId: string): Promise<any> => { throw new Error('Use createItem'); },
  removeFromWishlist: async (id: string): Promise<void> => wishlistService.deleteItem(id),
};

export const setWatchService = {
  getAll: async (): Promise<any[]> => {
    const response = await apiClient.get<any>('/set-watches');
    return response.data ?? [];
  },

  toggle: async (game: string, set_name: string): Promise<{ watching: boolean }> => {
    const response = await apiClient.post<any>('/set-watches/toggle', { game, set_name });
    return response.data ?? { watching: false };
  },
};

export interface EmailPreferences {
  auction_won:     boolean;
  auction_ended:   boolean;
  auction_expired: boolean;
  wishlist_match:  boolean;
  wishlist_seller: boolean;
  set_watch_match: boolean;
  new_message:     boolean;
  trade_offer:     boolean;
  order_update:    boolean;
}

export const paymentService = {
  createCheckout: async (listingId: string): Promise<{ url: string; orderId: string }> => {
    const response = await apiClient.post<any>('/payments/checkout', { listing_id: listingId });
    return response.data;
  },

  onboardConnect: async (): Promise<{ url: string }> => {
    const response = await apiClient.post<any>('/payments/connect/onboard');
    return response.data;
  },

  getConnectStatus: async (): Promise<{
    connected: boolean;
    onboarding_complete: boolean;
    charges_enabled?: boolean;
    payouts_enabled?: boolean;
  }> => {
    const response = await apiClient.get<any>('/payments/connect/status');
    return response.data;
  },

  getOrders: async (): Promise<any[]> => {
    const response = await apiClient.get<any>('/orders');
    return response.data ?? [];
  },

  getOrder: async (id: string): Promise<any> => {
    const response = await apiClient.get<any>(`/orders/${id}`);
    return response.data;
  },

  addTracking: async (orderId: string, trackingNumber: string): Promise<void> => {
    await apiClient.patch(`/orders/${orderId}/tracking`, { tracking_number: trackingNumber });
  },

  markDelivered: async (orderId: string): Promise<void> => {
    await apiClient.patch(`/orders/${orderId}/delivered`);
  },
};

export const deckService = {
  getDecks: async (filters?: { game?: string; format?: string }) => {
    const params = new URLSearchParams();
    if (filters?.game)   params.set('game', filters.game);
    if (filters?.format) params.set('format', filters.format);
    const response = await apiClient.get<any>(`/decks?${params}`);
    return response.data ?? [];
  },

  getDeck: async (id: string) => {
    const response = await apiClient.get<any>(`/decks/${id}`);
    return response.data;
  },

  createDeck: async (data: { name: string; game: string; description?: string; format?: string; archetype?: string }) => {
    const response = await apiClient.post<any>('/decks', data);
    return response.data;
  },

  updateDeck: async (id: string, data: Partial<{ name: string; description: string; format: string; archetype: string; is_public: boolean; is_competitive: boolean }>) => {
    const response = await apiClient.patch<any>(`/decks/${id}`, data);
    return response.data;
  },

  deleteDeck: async (id: string) => {
    await apiClient.delete(`/decks/${id}`);
  },

  addCard: async (deckId: string, data: { card_id: string; quantity: number; category?: string }) => {
    const response = await apiClient.post<any>(`/decks/${deckId}/cards`, data);
    return response.data;
  },

  updateCard: async (deckId: string, cardId: string, data: { quantity: number; category?: string }) => {
    const response = await apiClient.patch<any>(`/decks/${deckId}/cards/${cardId}`, data);
    return response.data;
  },

  removeCard: async (deckId: string, cardId: string, category = 'Main Deck') => {
    await apiClient.delete(`/decks/${deckId}/cards/${cardId}?category=${encodeURIComponent(category)}`);
  },

  getStats: async (deckId: string) => {
    const response = await apiClient.get<any>(`/decks/${deckId}/stats`);
    return response.data;
  },

  getMissingCards: async (deckId: string) => {
    const response = await apiClient.get<any>(`/decks/${deckId}/missing-cards`);
    return response.data;
  },

  getTemplates: async (filters?: { game?: string; tier?: string }) => {
    const params = new URLSearchParams();
    if (filters?.game) params.set('game', filters.game);
    if (filters?.tier) params.set('tier', filters.tier);
    const response = await apiClient.get<any>(`/deck-templates?${params}`);
    return response.data ?? [];
  },

  createFromTemplate: async (templateId: string, name?: string) => {
    const response = await apiClient.post<any>(`/decks/from-template/${templateId}`, { name });
    return response.data;
  },

  addCardByName: async (deckId: string, data: { name: string; quantity?: number; category?: string }) => {
    const response = await apiClient.post<any>(`/decks/${deckId}/cards/by-name`, data);
    return response.data;
  },

  batchAddCards: async (deckId: string, cards: { card_id: string; quantity: number; category?: string }[]) => {
    const response = await apiClient.post<any>(`/decks/${deckId}/cards/batch`, { cards });
    return response.data;
  },

  populateDeck: async (deckId: string, data: { query: string; limit?: number; category?: string; preview?: boolean }): Promise<{
    added: { name: string; card_id: string; image_url?: string }[];
    skipped: string[];
    total_found: number;
    game_mismatch?: { detected: string; deck: string } | null;
    suggestion?: string;
  }> => {
    const response = await apiClient.post<any>(`/decks/${deckId}/populate`, data);
    return response.data;
  },

  clearCards: async (deckId: string): Promise<{ removed: number }> => {
    const response = await apiClient.delete<any>(`/decks/${deckId}/cards`);
    return response.data;
  },
};

export const emailPreferencesService = {
  get: async (): Promise<EmailPreferences> => {
    const response = await apiClient.get<any>('/settings/email-preferences');
    return response.data;
  },

  update: async (prefs: Partial<EmailPreferences>): Promise<EmailPreferences> => {
    const response = await apiClient.put<any>('/settings/email-preferences', prefs);
    return response.data;
  },
};


export const progressionService = {
  // Level & XP
  getUserLevel: async (userId: string): Promise<UserLevel> => {
    const response = await apiClient.get<any>(`/users/${userId}/level`);
    return response.data.data ?? response.data;
  },

  getXpHistory: async (userId: string, limit = 20): Promise<XpHistoryEntry[]> => {
    const response = await apiClient.get<any>(`/users/${userId}/xp-history?limit=${limit}`);
    return response.data.data ?? response.data;
  },

  awardXp: async (action: string, description?: string, relatedEntityType?: string, relatedEntityId?: string): Promise<AwardXpResult> => {
    const response = await apiClient.post<any>('/xp/award', { action, description, relatedEntityType, relatedEntityId });
    return response.data.data ?? response.data;
  },

  // Trophies
  getAllTrophies: async (): Promise<TrophyDefinition[]> => {
    const response = await apiClient.get<any>('/trophies');
    return response.data.data ?? response.data;
  },

  getUserTrophies: async (userId: string): Promise<UserTrophy[]> => {
    const response = await apiClient.get<any>(`/users/${userId}/trophies`);
    return response.data.data ?? response.data;
  },

  getUserTrophyProgress: async (userId: string): Promise<TrophyWithProgress[]> => {
    const response = await apiClient.get<any>(`/users/${userId}/trophy-progress`);
    return response.data.data ?? response.data;
  },

  checkTrophies: async (requirementTypes: string[]): Promise<{ awarded: Array<{ key: string; name: string; tier: number; bonusXp: number }> }> => {
    const response = await apiClient.post<any>('/trophies/check', { requirementTypes });
    return response.data.data ?? response.data;
  },

  toggleTrophyDisplay: async (userId: string, trophyId: string, isDisplayed: boolean): Promise<void> => {
    await apiClient.patch(`/users/${userId}/trophies/${trophyId}`, { is_displayed: isDisplayed });
  },

  // Reputation
  getUserReputation: async (userId: string): Promise<UserReputation> => {
    const response = await apiClient.get<any>(`/users/${userId}/reputation`);
    return response.data.data ?? response.data;
  },

  // Leaderboards
  getLeaderboard: async (type: 'levels' | 'reputation' | 'trophies', limit = 50): Promise<LeaderboardEntry[]> => {
    const response = await apiClient.get<any>(`/leaderboards/${type}?limit=${limit}`);
    return response.data.data ?? response.data;
  },
};

// Moderation Services
export type ReportEntityType = 'listing' | 'message' | 'profile' | 'review' | 'forum_post';
export type ReportReason = 'spam' | 'scam' | 'harassment' | 'inappropriate' | 'copyright' | 'other';

export const moderationService = {
  submitReport: async (data: {
    reported_entity_type: ReportEntityType;
    reported_entity_id: string;
    reported_user_id: string;
    reason: ReportReason;
    description?: string;
  }): Promise<void> => {
    await apiClient.post('/reports', data);
  },

  blockUser: async (blockedUserId: string, reason?: string): Promise<void> => {
    await apiClient.post('/users/block', { blocked_user_id: blockedUserId, reason });
  },

  unblockUser: async (blockedUserId: string): Promise<void> => {
    await apiClient.delete(`/users/block/${blockedUserId}`);
  },

  getBlockedUsers: async (): Promise<any[]> => {
    const response = await apiClient.get<any>('/users/blocked');
    return response.data?.data ?? [];
  },

  // Admin
  getReports: async (status?: string, limit = 50, offset = 0): Promise<any[]> => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (status) params.set('status', status);
    const response = await apiClient.get<any>(`/admin/reports?${params}`);
    return response.data?.data ?? [];
  },

  reviewReport: async (id: string, data: {
    status: 'resolved' | 'dismissed';
    action_taken: 'removed' | 'warned' | 'banned' | 'no_action';
    resolution_notes?: string;
  }): Promise<void> => {
    await apiClient.patch(`/admin/reports/${id}`, data);
  },

  banUser: async (userId: string, reason: string, duration?: number): Promise<void> => {
    await apiClient.post(`/admin/users/${userId}/ban`, { reason, duration });
  },

  unbanUser: async (userId: string): Promise<void> => {
    await apiClient.post(`/admin/users/${userId}/unban`);
  },
};
